import * as THREE from 'three';
import { Visualizer } from '../visualizer.js';
import { safeZoneUniforms, SAFE_ZONE_VERTEX_CHUNK } from '../shaders/safeZone.glsl.js';

// Shared-rig contract (see the framing addendum). 'forbidden': every light in
// this piece is analytic, evaluated in the fragment shader. A rig's cones would
// wash out the only thing on screen.
const RIG = 'forbidden';
export { RIG };

// Orchestrator registry entry (see visualizer-registry.js).
export const PROFILE = {
  id: 'vinylgroove',
  family: 'object',
  energyLevel: 0.35,
  bpmMin: 60,
  bpmMax: 125,
  movement: 0.30,
  danceability: 0.50,
  tonality: 0.80,
  enabled: true
};

// VinylGroove: a record in close-up, tilted, its grooves catching light as
// bright arcs that sweep across the surface. No deck, no tonearm, no set.
//
// This is the only visualizer in the library that renders a LIT SURFACE. Every
// other one is additive light on black or flat graphics; here the material IS
// the subject, and it is the anisotropic reflection off the grooves that makes
// the image.
//
// THE ANISOTROPIC HIGHLIGHT is the whole piece. The grooves are concentric
// circles, so the groove direction at any point is the TANGENT - and Kajiya-Kay
// shading against that tangent produces the long ARC a record actually shows,
// not the round blob an isotropic Blinn-Phong lobe would give.
//
// PERFORMANCE SHAPE: the disc covers roughly half the frame, so this is
// fill-rate bound like the full-screen pieces. Cost is ~30 ops plus one
// specular evaluation per source. pow() with a variable exponent is banned by
// the contract, so the exponent is a compile-time constant unrolled into six
// multiplies. mediump throughout.

// 12, not 6. At the specified camera the frame is 10.5 units wide at the
// disc's depth, so a radius-6 disc sits entirely inside it with black margins
// either side - it cannot meet the "overflows left and right" requirement.
// Enlarging rather than moving the camera keeps the specified viewing angle
// exactly, and the label scales with it to a real record's 1:6 ratio.
const DISC_RADIUS = 12;
const DISC_CENTER = [0, 1.2, -1];
// -1.15 rad off the camera axis: the disc is seen about 24 degrees above
// edge-on, which is the angle at which the groove highlight reads as an arc
// rather than as a ring.
const DISC_TILT = -1.15;
const CAMERA_POS = [0, 3.2, 7.5];
const CAMERA_LOOK = [0, 1.6, -1];

const LABEL_RADIUS = 2.0;    // 1:6 of the disc, as on a real 12-inch record
const SPINDLE_RADIUS = 0.12;
const VINYL_BASE = 0.012;   // near-black, but not zero: the disc must have a body
const LABEL_BASE = 0.05;    // matte, a touch lighter than the vinyl
const GROOVE_FREQUENCY = 260.0;
const GROOVE_AMPLITUDE = 0.06;
const GROOVE_FADE_K = 0.06;

// Orbit of the light sources around the disc.
const LIGHT_ORBIT_RADIUS = 9;
const LIGHT_HEIGHT = 7;
// Incommensurable ratios, so the three arcs never fall back into the same
// relative arrangement and the pattern does not visibly loop.
const LIGHT_SPEEDS = [1.0, 0.6180, 0.4142, 0.7862];
const LIGHT_BASE_SPEED = 0.16;
const SPREAD_MAX = 2.1; // radians between neighbouring sources at mid-track
const FOURTH_SOURCE_ENERGY = 0.6;

// One revolution every 4 beats: 32rpm at 128bpm, which is both musical and
// within a hair of a real 33rpm record.
const BEATS_PER_REVOLUTION = 4;
const RPM_NO_BPM = 33.3;

const SCRATCH_WIDTH_PX = 1.5;
const SCRATCH_ARC_MIN = 0.05;
const SCRATCH_ARC_MAX = 0.40;
const SCRATCH_GAIN = 2.5;

const BEAT_PULSE_AMOUNT = 0.4;
const BEAT_PULSE_DECAY = 11;

// Remaps the shared safe zone's own 0.32 floor onto the 0.35 this piece wants,
// without touching the shared constant every other visualizer reads.
const SAFE_FLOOR = 0.35;
const SHARED_SAFE_FLOOR = 0.32;

const FALLBACK_COLORS = [0xff7a2f, 0x2fd8ff, 0xff2fb0, 0xffe08a];

const TIER_LAYOUT = {
  0: { sources: 2, scratches: 20, grooves: false, fourth: false },
  1: { sources: 2, scratches: 30, grooves: true, fourth: false },
  2: { sources: 3, scratches: 45, grooves: true, fourth: true }
};
const DEFAULT_TIER = 1;


// Kajiya-Kay against the groove tangent. sin(T,H) peaks where the half-vector
// is perpendicular to the groove, and because the grooves are concentric that
// locus is a long curve across the disc - the arc. An isotropic dot(N,H) lobe
// would put a round blob there instead, which is criterion 1's own test.
// The exponent has to be LARGE. sin(T,H) sits close to 1 across most of the
// disc - the tangent is circumferential and the half-vector is mostly along
// the normal - so a modest exponent leaves the whole surface lit and only the
// narrow T-parallel bands dark, which is the inverse of a record. Raising it
// to 2^10 means only the locus where T is genuinely perpendicular to H
// survives, and that locus is the thin arc.
// Written as repeated squaring: pow() with a variable exponent is banned by
// the performance contract, and 10 multiplies are cheaper than pow anyway.
const SPEC_SQUARINGS = 10;      // 2^10 = 1024, the disc's tight arc
const SCRATCH_SQUARINGS = 6;    // 2^6  = 64,   broader, so scratches flare
const squarings = (n) => Array.from({ length: n }, () => '    s = s * s;').join('\n');
// Exported, though nothing imports it at the moment: VinylWall did, to shade
// its eleven records with this exact lobe rather than a second copy of it, and
// that visualizer has since been removed. Left exported because the chunk is
// self-contained and the next wall of records should reuse it rather than
// rewrite it.
export const SPEC_CHUNK = `
  float grooveSpec(vec3 T, vec3 L, vec3 V) {
    vec3 H = normalize(L + V);
    float dotTH = dot(T, H);
    float sinTH = sqrt(max(1.0 - dotTH * dotTH, 0.0));
    float s = sinTH;
${squarings(SPEC_SQUARINGS)}
    return s;
  }

  // Same lobe, much broader - the scratches are meant to flare as they cross a
  // highlight, not to hold a hairline.
  float scratchSpec(vec3 T, vec3 L, vec3 V) {
    vec3 H = normalize(L + V);
    float dotTH = dot(T, H);
    float sinTH = sqrt(max(1.0 - dotTH * dotTH, 0.0));
    float s = sinTH;
${squarings(SCRATCH_SQUARINGS)}
    return s;
  }
`;

const discVertexShader = `
  precision mediump float;

  uniform mat3 uNormalMat;

  varying vec2 vPlane;      // position within the disc's own plane
  varying vec3 vWorld;
  varying vec3 vNormalW;
  varying float vSafeAtt;

  ${SAFE_ZONE_VERTEX_CHUNK}

  void main() {
    // CircleGeometry lies in local XY with its normal along +Z, so the plane
    // coordinates are just position.xy and the radius is their length.
    vPlane = position.xy;
    vec4 world = modelMatrix * vec4(position, 1.0);
    vWorld = world.xyz;
    vNormalW = normalize(uNormalMat * vec3(0.0, 0.0, 1.0));
    gl_Position = projectionMatrix * viewMatrix * world;
    vSafeAtt = safeZoneAttenuation(gl_Position);
  }
`;

function buildDiscFragmentShader(layout) {
  const sources = layout.sources;
  // Unrolled: one block per compiled source, no loop.
  let specBlocks = '';
  for (let i = 0; i < sources; i++) {
    specBlocks += `
    {
      vec3 L = normalize(uLightPos${i} - vWorld);
      float sp = grooveSpec(T, L, V);
      spec += uLightColor${i} * sp * uLightGain${i};
    }`;
  }
  const fourth = layout.fourth ? `
    // The conditional fourth source. Guarded by a UNIFORM, so the branch is
    // coherent across the whole frame and costs nothing when it is off - as
    // opposed to multiplying by a zero gain, which would pay for the specular
    // evaluation on every pixel regardless.
    if (uFourthActive > 0.5) {
      vec3 L3 = normalize(uLightPos3 - vWorld);
      spec += uLightColor3 * grooveSpec(T, L3, V) * uLightGain3;
    }` : '';

  let uniforms = '  uniform vec3 uRadialBasisX;\n  uniform vec3 uRadialBasisY;\n';
  for (let i = 0; i < sources; i++) {
    uniforms += `  uniform vec3 uLightPos${i};\n  uniform vec3 uLightColor${i};\n  uniform float uLightGain${i};\n`;
  }
  if (layout.fourth) {
    uniforms += `  uniform vec3 uLightPos3;\n  uniform vec3 uLightColor3;\n  uniform float uLightGain3;\n  uniform float uFourthActive;\n`;
  }

  const grooves = layout.grooves ? `
    // Micro-grooves. At this frequency the far half of the disc would alias
    // savagely, so the perturbation is faded out with view distance: near
    // grooves are textured, far ones smooth. Physically right, and it kills
    // the moire without screen-space derivatives, which WebGL1 does not have.
    float grooveFade = 1.0 / (1.0 + viewDist * viewDist * ${GROOVE_FADE_K.toFixed(3)});
    float groove = sin(r * ${GROOVE_FREQUENCY.toFixed(1)});
    N = normalize(N + radialW * groove * ${GROOVE_AMPLITUDE.toFixed(3)} * grooveFade * onVinyl);
  ` : '';

  return `
  precision mediump float;

${uniforms}
  varying vec2 vPlane;
  varying vec3 vWorld;
  varying vec3 vNormalW;
  varying float vSafeAtt;

  ${SPEC_CHUNK}

  void main() {
    float r = length(vPlane);
    // 1 on the grooved area, 0 on the label - the label is pressed paper, it
    // has no grooves and therefore no anisotropic highlight at all.
    float onVinyl = step(${LABEL_RADIUS.toFixed(3)}, r);

    vec3 V = normalize(cameraPosition - vWorld);
    float viewDist = length(cameraPosition - vWorld);
    vec3 N = vNormalW;

    // Outward direction in the disc's own plane, carried into world space by
    // the disc's two in-plane basis vectors. The groove runs along the TANGENT
    // of a circle centred on the spindle - getting this wrong is precisely
    // what turns the arcs into round blobs.
    vec2 radial2 = r > 0.0001 ? vPlane / r : vec2(1.0, 0.0);
    vec3 radialW = normalize(uRadialBasisX * radial2.x + uRadialBasisY * radial2.y);
    ${grooves}
    vec3 T = normalize(cross(N, radialW));

    vec3 spec = vec3(0.0);
    ${specBlocks}
    ${fourth}

    vec3 base = mix(vec3(${LABEL_BASE.toFixed(3)}), vec3(${VINYL_BASE.toFixed(3)}), onVinyl);
    vec3 col = base + spec * onVinyl;

    // The spindle hole.
    col *= step(${SPINDLE_RADIUS.toFixed(3)}, r);

    // Safe zone as a multiplicative attenuation with this piece's own floor,
    // remapped off the shared chunk's 0.32 rather than editing that constant.
    float att = mix(${SAFE_FLOOR.toFixed(2)}, 1.0,
                    (vSafeAtt - ${SHARED_SAFE_FLOOR.toFixed(2)}) / ${(1 - SHARED_SAFE_FLOOR).toFixed(2)});
    gl_FragColor = vec4(col * att, 1.0);
  }
  `;
}

const scratchVertexShader = `
  precision mediump float;

  attribute float iRadius;
  attribute float iAngle;
  attribute float iArc;
  attribute float iSeed;

  uniform float uPixelScale;
  uniform vec3 uRadialBasisX;
  uniform vec3 uRadialBasisY;
  uniform mat3 uNormalMat;

  varying vec3 vWorld;
  varying vec3 vNormalW;
  varying vec3 vRadialW;
  varying float vArcT;

  void main() {
    float ang = iAngle + position.x * iArc;
    vec2 dir = vec2(cos(ang), sin(ang));
    vec2 plane = dir * iRadius;

    // Provisional world position, only so the view depth is known before the
    // screen-space thickness is worked out.
    vec4 probe = modelMatrix * vec4(plane, 0.0, 1.0);
    float viewDepth = max(-(viewMatrix * probe).z, 0.001);
    float thickness = ${SCRATCH_WIDTH_PX.toFixed(2)} * viewDepth / uPixelScale;

    plane += dir * (position.y * thickness);
    // Lifted a hair off the disc so it never z-fights with the surface it
    // sits on.
    vec4 world = modelMatrix * vec4(plane, 0.004, 1.0);
    vWorld = world.xyz;
    vNormalW = normalize(uNormalMat * vec3(0.0, 0.0, 1.0));
    vRadialW = normalize(uRadialBasisX * dir.x + uRadialBasisY * dir.y);
    // The along-arc coordinate, faded in the FRAGMENT shader. Computing
    // sin(position.x * PI) here instead gave 0 at BOTH vertices - the quad only
    // has x = 0 and x = 1 - so the interpolated value was zero everywhere and
    // every scratch was invisible.
    vArcT = position.x;

    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

function buildScratchFragmentShader(layout) {
  let blocks = '';
  for (let i = 0; i < layout.sources; i++) {
    blocks += `
    {
      vec3 L = normalize(uLightPos${i} - vWorld);
      spec += uLightColor${i} * scratchSpec(T, L, V) * uLightGain${i};
    }`;
  }
  let uniforms = '';
  for (let i = 0; i < layout.sources; i++) {
    uniforms += `  uniform vec3 uLightPos${i};\n  uniform vec3 uLightColor${i};\n  uniform float uLightGain${i};\n`;
  }
  return `
  precision mediump float;

${uniforms}
  varying vec3 vWorld;
  varying vec3 vNormalW;
  varying vec3 vRadialW;
  varying float vArcT;

  ${SPEC_CHUNK}

  void main() {
    vec3 V = normalize(cameraPosition - vWorld);
    vec3 T = normalize(cross(vNormalW, vRadialW));
    vec3 spec = vec3(0.0);
    ${blocks}
    // Softened at both ends so a scratch does not begin and end on a hard cut.
    float fade = sin(vArcT * 3.14159265);
    // Additive over the disc: all intensity in rgb, alpha 1 - additive
    // blending is (SRC_ALPHA, ONE) and carrying it in both would square it.
    gl_FragColor = vec4(spec * ${SCRATCH_GAIN.toFixed(2)} * fade, 1.0);
  }
  `;
}

export class VinylGrooveVisualizer extends Visualizer {
  constructor(options) {
    super();
    const tier = options && options.qualityTier != null ? options.qualityTier : DEFAULT_TIER;
    this._tier = TIER_LAYOUT[tier] ? tier : DEFAULT_TIER;
  }

  init(scene, camera) {
    this._scene = scene;
    this._camera = camera;
    camera.position.set(CAMERA_POS[0], CAMERA_POS[1], CAMERA_POS[2]);
    camera.lookAt(CAMERA_LOOK[0], CAMERA_LOOK[1], CAMERA_LOOK[2]);

    const layout = TIER_LAYOUT[this._tier];
    this._layout = layout;

    this._uniforms = Object.assign({}, safeZoneUniforms, {
      uPixelScale: { value: 1 },
      uNormalMat: { value: new THREE.Matrix3() },
      // The disc's own in-plane axes, in world space. The tangent is derived
      // from these rather than from a normal matrix applied per fragment.
      uRadialBasisX: { value: new THREE.Vector3(1, 0, 0) },
      uRadialBasisY: { value: new THREE.Vector3(0, 1, 0) }
    });
    const slots = layout.fourth ? 4 : layout.sources;
    for (let i = 0; i < slots; i++) {
      this._uniforms['uLightPos' + i] = { value: new THREE.Vector3() };
      this._uniforms['uLightColor' + i] = { value: new THREE.Color(FALLBACK_COLORS[i]) };
      this._uniforms['uLightGain' + i] = { value: 1 };
    }
    if (layout.fourth) this._uniforms.uFourthActive = { value: 0 };

    this._buildDisc(layout);
    this._buildScratches(layout);

    this._time = 0;
    this._spin = 0;
    this._lastPaletteRef = null;
  }

  _buildDisc(layout) {
    const geometry = new THREE.CircleGeometry(DISC_RADIUS, 128);
    this._discGeometry = geometry;
    this._discMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: discVertexShader,
      fragmentShader: buildDiscFragmentShader(layout),
      // NOT additive: the record is an opaque object, and that is exactly what
      // separates this piece from everything else in the library.
      transparent: false,
      depthWrite: true,
      depthTest: true,
      side: THREE.DoubleSide
    });
    this._disc = new THREE.Mesh(geometry, this._discMaterial);
    this._disc.position.set(DISC_CENTER[0], DISC_CENTER[1], DISC_CENTER[2]);
    // Euler XYZ applies as Rx * Ry * Rz, so Z spins the disc in its OWN plane
    // and X then tilts the result - which is the order this needs.
    this._disc.rotation.set(DISC_TILT, 0, 0);
    this._disc.frustumCulled = false;
    this._scene.add(this._disc);
  }

  // A circular groove is invariant under rotation: with the disc alone,
  // spinning it produces no visible change whatsoever. These are what carry the
  // rotation, and the wear.
  _buildScratches(layout) {
    const count = layout.scratches;
    const radii = new Float32Array(count);
    const angles = new Float32Array(count);
    const arcs = new Float32Array(count);
    const seeds = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      // sqrt keeps them evenly spread over the AREA rather than crowding the
      // spindle, and they only exist on the grooved part.
      radii[i] = LABEL_RADIUS + Math.sqrt(Math.random()) * (DISC_RADIUS - LABEL_RADIUS - 0.1);
      angles[i] = Math.random() * Math.PI * 2;
      arcs[i] = SCRATCH_ARC_MIN + Math.random() * (SCRATCH_ARC_MAX - SCRATCH_ARC_MIN);
      seeds[i] = Math.random();
    }

    // x runs 0..1 along the arc, y is -0.5..0.5 across its width.
    const quad = new Float32Array([0, -0.5, 0, 1, -0.5, 0, 1, 0.5, 0, 0, 0.5, 0]);
    const geometry = new THREE.InstancedBufferGeometry();
    geometry.setIndex([0, 1, 2, 0, 2, 3]);
    geometry.setAttribute('position', new THREE.BufferAttribute(quad, 3));
    geometry.setAttribute('iRadius', new THREE.InstancedBufferAttribute(radii, 1));
    geometry.setAttribute('iAngle', new THREE.InstancedBufferAttribute(angles, 1));
    geometry.setAttribute('iArc', new THREE.InstancedBufferAttribute(arcs, 1));
    geometry.setAttribute('iSeed', new THREE.InstancedBufferAttribute(seeds, 1));
    geometry.instanceCount = count;
    geometry.boundingSphere = new THREE.Sphere(new THREE.Vector3(0, 0, 0), DISC_RADIUS + 1);
    this._scratchGeometry = geometry;

    this._scratchMaterial = new THREE.ShaderMaterial({
      uniforms: this._uniforms,
      vertexShader: scratchVertexShader,
      fragmentShader: buildScratchFragmentShader(layout),
      transparent: true,
      depthWrite: false,
      depthTest: true,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending
    });
    this._scratches = new THREE.Mesh(geometry, this._scratchMaterial);
    this._scratches.frustumCulled = false;
    // Child of the disc, so they inherit its spin for free - no second
    // transform to keep in sync.
    this._disc.add(this._scratches);
  }

  _applyPalette(profile) {
    const palette = profile.artworkPalette;
    const vivid = (palette && palette.vividColors) || [];
    const slots = this._layout.fourth ? 4 : this._layout.sources;
    for (let i = 0; i < slots; i++) {
      const c = this._uniforms['uLightColor' + i].value;
      c.set(vivid.length > 0 ? vivid[i % vivid.length] : FALLBACK_COLORS[i]);
    }
  }

  update(deltaTime, profile) {
    const paused = profile.playbackState !== 'PLAYING';
    if (!paused) this._time += deltaTime;

    const bpm = profile.trackFeatures && profile.trackFeatures.bpm;
    // One revolution every BEATS_PER_REVOLUTION beats when the tempo is known;
    // a real 33rpm otherwise.
    const rps = bpm ? (bpm / 60) / BEATS_PER_REVOLUTION : RPM_NO_BPM / 60;
    if (!paused) {
      this._spin += deltaTime * rps * Math.PI * 2;
      if (this._spin > Math.PI * 2) this._spin -= Math.PI * 2;
    }
    this._disc.rotation.set(DISC_TILT, 0, this._spin);
    this._disc.updateMatrixWorld(true);

    // The disc's own in-plane axes carried into world space - this is what the
    // shaders build the groove tangent from.
    const m = this._disc.matrixWorld.elements;
    this._uniforms.uRadialBasisX.value.set(m[0], m[1], m[2]).normalize();
    this._uniforms.uRadialBasisY.value.set(m[4], m[5], m[6]).normalize();
    this._uniforms.uNormalMat.value.setFromMatrix4(this._disc.matrixWorld).invert().transpose();

    if (profile.artworkPalette !== this._lastPaletteRef) {
      this._lastPaletteRef = profile.artworkPalette;
      this._applyPalette(profile);
    }

    // Sources bunched at the start and end of a track (one broad arc) and
    // spread through the middle (three distinct arcs sweeping separately).
    const intensity = profile.director.intensity;
    const spread = intensity * SPREAD_MAX;
    const pulse = profile.director.pulse(BEAT_PULSE_AMOUNT, BEAT_PULSE_DECAY);
    const slots = this._layout.fourth ? 4 : this._layout.sources;
    for (let i = 0; i < slots; i++) {
      const angle = this._time * LIGHT_BASE_SPEED * LIGHT_SPEEDS[i] + i * spread;
      this._uniforms['uLightPos' + i].value.set(
        DISC_CENTER[0] + Math.cos(angle) * LIGHT_ORBIT_RADIUS,
        LIGHT_HEIGHT,
        DISC_CENTER[2] + Math.sin(angle) * LIGHT_ORBIT_RADIUS
      );
      this._uniforms['uLightGain' + i].value = pulse;
    }

    // energy is one of the fields that is simply absent for many tracks - when
    // it is, nothing happens, rather than a default standing in for it.
    if (this._layout.fourth) {
      const energy = profile.trackFeatures && profile.trackFeatures.energy;
      this._uniforms.uFourthActive.value =
        energy != null && energy / 100 > FOURTH_SOURCE_ENERGY ? 1 : 0;
    }

    const canvasPixelHeight = window.innerHeight * Math.min(window.devicePixelRatio || 1, 2);
    const fovRadians = THREE.MathUtils.degToRad(this._camera.fov);
    this._uniforms.uPixelScale.value = canvasPixelHeight / (2 * Math.tan(fovRadians / 2));
  }

  dispose() {
    this._disc.remove(this._scratches);
    this._scratchGeometry.dispose();
    this._scratchMaterial.dispose();
    this._scratches = null;
    this._scene.remove(this._disc);
    this._discGeometry.dispose();
    this._discMaterial.dispose();
    this._disc = null;
  }
}
